import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Globe from "react-globe.gl";
import { useApi } from "@/lib/useApi";
import { useLang } from "@/context/AppContext";
import { PageHead } from "@/pages/app/Dashboard";
import { GlobeFilters, applyFilters, DEFAULT_FILTERS } from "@/components/GlobeFilters";

const CITIES = {
  madrid: [40.42, -3.7], barcelona: [41.39, 2.17], valencia: [39.47, -0.38], sevilla: [37.39, -5.98], bilbao: [43.26, -2.93], lisboa: [38.72, -9.14], lisbon: [38.72, -9.14],
  london: [51.51, -0.13], londres: [51.51, -0.13], paris: [48.86, 2.35], berlin: [52.52, 13.4], amsterdam: [52.37, 4.9], roma: [41.9, 12.5], rome: [41.9, 12.5], milan: [45.46, 9.19], zurich: [47.37, 8.54], dublin: [53.35, -6.26],
  "new york": [40.71, -74.0], "nueva york": [40.71, -74.0], miami: [25.76, -80.19], "san francisco": [37.77, -122.42], toronto: [43.65, -79.38], "mexico city": [19.43, -99.13], "ciudad de mexico": [19.43, -99.13], cdmx: [19.43, -99.13],
  bogota: [4.71, -74.07], medellin: [6.24, -75.58], lima: [-12.05, -77.04], santiago: [-33.45, -70.67], "buenos aires": [-34.6, -58.38], "sao paulo": [-23.55, -46.63], quito: [-0.18, -78.47], caracas: [10.48, -66.9],
  dubai: [25.2, 55.27], singapore: [1.35, 103.82], tokyo: [35.68, 139.69], sydney: [-33.87, 151.21], "hong kong": [22.32, 114.17], mumbai: [19.08, 72.88],
};
const STATE_COLORS = { proposed: "#F59E0B", confirmed: "#06B6D4", authorized: "#EAB308", active: "#10B981", completed: "#94A3B8", suspended: "#FB923C", terminated: "#EF4444" };

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
const geo = (loc) => {
  const k = norm(loc);
  if (CITIES[k]) return CITIES[k];
  const hit = Object.keys(CITIES).find((c) => k.includes(c));
  if (hit) return CITIES[hit];
  let h = 0;
  for (const ch of k || "?") h = (h * 31 + ch.charCodeAt(0)) % 100000;
  return [((h % 120) - 60), ((h * 7) % 360) - 180];
};

const EMPTY_NETWORK = { arcs: [] };

const buildGraph = (allArcs, filters) => {
  const pts = {};
  const arcs = applyFilters(allArcs, filters).map((a) => {
    const [sLat, sLng] = geo(a.person.location);
    const [eLat, eLng] = geo(a.company.location);
    const jitter = a.person.location && norm(a.person.location) === norm(a.company.location) ? 1.2 : 0;
    pts[a.person.id] = { ...a.person, kind: "person", lat: sLat, lng: sLng };
    pts[a.company.id] = { ...a.company, kind: "company", lat: eLat + jitter, lng: eLng + jitter };
    return { ...a, startLat: sLat, startLng: sLng, endLat: eLat + jitter, endLng: eLng + jitter, color: STATE_COLORS[a.state] };
  });
  return { arcs, points: Object.values(pts) };
};

const useBoxSize = (box) => {
  const [size, setSize] = useState({ w: 800, h: 600 });
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: Math.max(480, window.innerHeight - 260) }));
    if (box.current) ro.observe(box.current);
    return () => ro.disconnect();
  }, [box]);
  return size;
};

const useAutoRotate = (ref, width) => {
  useEffect(() => {
    if (!ref.current) return;
    const c = ref.current.controls();
    c.autoRotate = true;
    c.autoRotateSpeed = 0.5;
    ref.current.pointOfView({ lat: 30, lng: -20, altitude: 2.2 }, 0);
  }, [ref, width]);
};

export default function NetworkGlobe() {
  const { t } = useLang();
  const nav = useNavigate();
  const ref = useRef(null);
  const box = useRef(null);
  const [hover, setHover] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const { data } = useApi("/network", { initial: EMPTY_NETWORK });
  const size = useBoxSize(box);
  useAutoRotate(ref, size.w);
  const { arcs, points } = useMemo(() => buildGraph(data.arcs, filters), [data, filters]);
  const rings = useMemo(() => points.filter((p) => p.trust >= 75), [points]);

  return (
    <div>
      <PageHead title={t("globe_title")} sub={t("globe_sub")} />
      <GlobeFilters arcs={data.arcs} f={filters} setF={setFilters} shown={arcs.length} />
      <div ref={box} className="relative overflow-hidden rounded-md border border-white/[0.08] bg-[#05080e]" data-testid="network-globe">
        <Globe
          ref={ref}
          width={size.w}
          height={size.h}
          backgroundColor="rgba(0,0,0,0)"
          globeImageUrl="https://unpkg.com/three-globe/example/img/earth-night.jpg"
          bumpImageUrl="https://unpkg.com/three-globe/example/img/earth-topology.png"
          atmosphereColor="#10B981"
          atmosphereAltitude={0.18}
          arcsData={arcs}
          arcColor={(a) => [a.color, "#06B6D4"]}
          arcStroke={(a) => (a.state === "active" ? 1.1 : 0.6)}
          arcDashLength={0.45}
          arcDashGap={0.15}
          arcDashAnimateTime={(a) => 4000 - a.score * 25}
          arcAltitudeAutoScale={0.45}
          arcLabel={(a) => `${a.person.name} ↔ ${a.company.name} · ${a.state} · ${a.score}`}
          onArcClick={(a) => nav(`/app/connections/${a.id}`)}
          pointsData={points}
          pointColor={(p) => (p.kind === "company" ? "#EAB308" : "#10B981")}
          pointAltitude={(p) => 0.02 + p.trust / 1000}
          pointRadius={0.45}
          pointLabel={(p) => `${p.name} · ${p.location || t("no_location")} · trust ${p.trust}`}
          onPointHover={setHover}
          onPointClick={(p) => nav(`/app/profiles/${p.id}`)}
          ringsData={rings}
          ringColor={() => (tt) => `rgba(16,185,129,${1 - tt})`}
          ringMaxRadius={3}
          ringPropagationSpeed={1.5}
          ringRepeatPeriod={1400}
        />
        {hover && (
          <div className="pointer-events-none absolute left-4 top-4 rounded-md border border-white/10 glass px-4 py-3" data-testid="globe-hover-card">
            <p className="font-display font-bold text-white">{hover.name}</p>
            <p className="font-mono text-[11px] text-slate-400">{hover.location || t("no_location")} · {t("trust")} {hover.trust}</p>
          </div>
        )}
        {!data.arcs.length && <p className="absolute bottom-4 left-4 text-sm text-slate-500" data-testid="globe-empty">{t("empty")}</p>}
      </div>
    </div>
  );
}
